import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/index.css";
import "@/App.css";

import { AuthProvider, useAuth } from "@/auth/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ActionBoard from "@/pages/ActionBoard";
import Leads from "@/pages/Leads";
import Quotes from "@/pages/Quotes";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import Dispatch from "@/pages/Dispatch";
import { Invoices, Payments } from "@/pages/Money";
import CashLog from "@/pages/CashLog";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Floor from "@/pages/Floor";
import DeliveryRoute from "@/pages/DeliveryRoute";
import Reconciliation from "@/pages/Reconciliation";
import Samples from "@/pages/Samples";
import CustomerDetail from "@/pages/CustomerDetail";
import VendorDetail from "@/pages/VendorDetail";
import { Customers, Vendors, Products, Complaints, Expenses } from "@/pages/SimplePages";

// Public pages (no auth)
import PublicQuote from "@/pages/public/PublicQuote";
import PublicArtwork from "@/pages/public/PublicArtwork";
import PublicRating from "@/pages/public/PublicRating";
import ClientLogin from "@/pages/client/ClientLogin";
import ClientPortal from "@/pages/client/ClientPortal";
import { PrintInvoice, PrintQuote, PrintOrderForm, PrintJobCard, PrintPO } from "@/pages/print/PrintDocs";

function Protected({ children, roles }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="p-8 text-[#757169]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  const cashHome = user?.role === "cash";
  const canOps = ["admin","team","production"].includes(user?.role);
  const canSales = ["admin","team","sales"].includes(user?.role);
  return (
    <Routes>
      {/* Public customer-facing routes (no login) */}
      <Route path="/q/:token" element={<PublicQuote />} />
      <Route path="/mockup/:token" element={<PublicArtwork />} />
      <Route path="/rate/:token" element={<PublicRating />} />

      {/* Client portal (customer OTP login) */}
      <Route path="/portal/login" element={<ClientLogin />} />
      <Route path="/portal" element={<ClientPortal />} />

      {/* Print routes (staff auth) */}
      <Route path="/print/invoice/:id" element={<Protected><PrintInvoice /></Protected>} />
      <Route path="/print/quote/:id" element={<Protected><PrintQuote /></Protected>} />
      <Route path="/print/order/:id" element={<Protected><PrintOrderForm /></Protected>} />
      <Route path="/print/job-card/:id" element={<Protected><PrintJobCard /></Protected>} />
      <Route path="/print/po/:id" element={<Protected><PrintPO /></Protected>} />

      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />

      <Route element={<Protected><Layout /></Protected>}>
        <Route index element={cashHome ? <Navigate to="/cash" replace /> : <ActionBoard />} />
        <Route path="leads" element={<Protected roles={["admin","team","sales"]}><Leads /></Protected>} />
        <Route path="quotes" element={<Protected roles={["admin","team","sales"]}><Quotes /></Protected>} />
        <Route path="orders" element={<Protected roles={["admin","team","production"]}><Orders /></Protected>} />
        <Route path="orders/:id" element={<Protected roles={["admin","team","production"]}><OrderDetail /></Protected>} />
        <Route path="floor" element={<Protected roles={["admin","team","production"]}><Floor /></Protected>} />
        <Route path="dispatch" element={<Protected roles={["admin","team","production"]}><Dispatch /></Protected>} />
        <Route path="invoices" element={<Protected roles={["admin","team","sales"]}><Invoices /></Protected>} />
        <Route path="payments" element={<Protected roles={["admin"]}><Payments /></Protected>} />
        <Route path="expenses" element={<Protected roles={["admin","team"]}><Expenses /></Protected>} />
        <Route path="customers" element={<Protected roles={["admin","team","sales"]}><Customers /></Protected>} />
        <Route path="customers/:id" element={<Protected roles={["admin","team","sales"]}><CustomerDetail /></Protected>} />
        <Route path="vendors/:id" element={<Protected roles={["admin","team","production"]}><VendorDetail /></Protected>} />
        <Route path="vendors" element={<Protected roles={["admin","team","production"]}><Vendors /></Protected>} />
        <Route path="products" element={<Protected roles={["admin","team","production"]}><Products /></Protected>} />
        <Route path="samples" element={<Protected roles={["admin","team","sales"]}><Samples /></Protected>} />
        <Route path="complaints" element={<Protected roles={["admin","team"]}><Complaints /></Protected>} />
        <Route path="reports" element={<Protected roles={["admin"]}><Reports /></Protected>} />
        <Route path="reconciliation" element={<Protected roles={["admin","team","production"]}><Reconciliation /></Protected>} />
        <Route path="delivery-route" element={<Protected roles={["admin","team","production"]}><DeliveryRoute /></Protected>} />
        <Route path="cash" element={<Protected roles={["admin","cash"]}><CashLog /></Protected>} />
        <Route path="settings" element={<Protected roles={["admin"]}><Settings /></Protected>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
